import { useState } from 'react'
import { XMarkIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import type { TableColumn } from '@/types/compatibility'

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  onExport: (options: ExportOptions) => void
  columns: TableColumn[]
  totalRows: number
}

export interface ExportOptions {
  format: 'csv' | 'json' | 'sql'
  columns: string[]
  includeHeaders: boolean
  limit?: number
  whereClause?: string
}

export function ExportModal({ isOpen, onClose, onExport, columns, totalRows }: ExportModalProps) {
  const [format, setFormat] = useState<'csv' | 'json' | 'sql'>('csv')
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => 
    columns.map(c => c.name).filter(Boolean)
  )
  const [includeHeaders, setIncludeHeaders] = useState(true)
  const [limitRows, setLimitRows] = useState(false)
  const [rowLimit, setRowLimit] = useState(1000)
  const [isExporting, setIsExporting] = useState(false)

  const toggleColumn = (columnName: string) => {
    if (selectedColumns.includes(columnName)) {
      setSelectedColumns(selectedColumns.filter(c => c !== columnName))
    } else {
      setSelectedColumns([...selectedColumns, columnName])
    }
  }

  const selectAllColumns = () => {
    setSelectedColumns(columns.map(c => c.name).filter(Boolean))
  }

  const deselectAllColumns = () => {
    setSelectedColumns([])
  }

  const handleExport = () => {
    if (selectedColumns.length === 0) {
      return
    }

    setIsExporting(true)
    try {
      const options: ExportOptions = {
        format,
        columns: selectedColumns,
        includeHeaders,
        limit: limitRows ? rowLimit : undefined
      }
      
      onExport(options)
      onClose()
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const getEstimatedSize = () => {
    const rowsToExport = limitRows ? Math.min(rowLimit, totalRows) : totalRows
    const columnsCount = selectedColumns.length
    
    // Rough estimation based on format
    let sizeKB
    if (format === 'csv') {
      sizeKB = (rowsToExport * columnsCount * 10) / 1024 // ~10 chars per cell
    } else if (format === 'json') {
      sizeKB = (rowsToExport * columnsCount * 20) / 1024 // ~20 chars per cell with JSON overhead
    } else {
      sizeKB = (rowsToExport * columnsCount * 25) / 1024 // SQL INSERT statements
    }

    return sizeKB < 1024 ? `~${Math.round(sizeKB)} KB` : `~${Math.round(sizeKB / 1024 * 10) / 10} MB`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <DocumentArrowDownIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Export Data
            </h2>
          </div>
          <button type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isExporting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Export Format
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'csv', label: 'CSV', description: 'Comma-separated values' },
                { value: 'json', label: 'JSON', description: 'JavaScript Object Notation' },
                { value: 'sql', label: 'SQL', description: 'INSERT statements' }
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    format === option.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    checked={format === option.value}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === 'csv' || v === 'json' || v === 'sql') {
                        setFormat(v)
                      } else {
                        setFormat('csv')
                      }
                    }}
                    className="sr-only"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {option.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {option.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Column Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Columns to Export ({selectedColumns.length} of {columns.length})
              </label>
              <div className="flex space-x-2">
                <button type="button"
                  onClick={selectAllColumns}
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  Select All
                </button>
                <button type="button"
                  onClick={deselectAllColumns}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                >
                  Deselect All
                </button>
              </div>
            </div>
            
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2">
                {columns.map((column) => {
                  if (!column.name) return null
                  return (
                    <label
                      key={column.name}
                      className="flex items-center space-x-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(column.name)}
                        onChange={() => { toggleColumn(column.name) }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-900 dark:text-white">
                        {column.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({column.type})
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="includeHeaders"
                checked={includeHeaders}
                onChange={(e) => { setIncludeHeaders(e.target.checked) }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="includeHeaders" className="text-sm text-gray-700 dark:text-gray-300">
                Include column headers
              </label>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="limitRows"
                  checked={limitRows}
                  onChange={(e) => { setLimitRows(e.target.checked) }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="limitRows" className="text-sm text-gray-700 dark:text-gray-300">
                  Limit number of rows
                </label>
              </div>
              
              {limitRows && (
                <div className="ml-6 flex items-center space-x-2">
                  <input
                    type="number"
                    value={rowLimit}
                    onChange={(e) => { setRowLimit(Math.max(1, Number.parseInt(e.target.value) || 1)) }}
                    min="1"
                    max={totalRows}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-500">
                    rows (max: {totalRows.toLocaleString()})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Export Summary
            </h4>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <div>Format: <span className="font-medium">{format.toUpperCase()}</span></div>
              <div>Columns: <span className="font-medium">{selectedColumns.length}</span></div>
              <div>
                Rows: <span className="font-medium">
                  {limitRows ? Math.min(rowLimit, totalRows).toLocaleString() : totalRows.toLocaleString()}
                </span>
              </div>
              <div>Estimated size: <span className="font-medium">{getEstimatedSize()}</span></div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button type="button"
            onClick={() => { handleExport(); }}
            disabled={isExporting || selectedColumns.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 flex items-center"
          >
            {isExporting && (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            )}
            Export Data
          </button>
        </div>
      </div>
    </div>
  )
}
